import { useFormContext, useWatch } from "react-hook-form";
import { useState, useMemo, useEffect } from "react";
import { CreditCard, Wallet, Repeat, Calendar, Clock, AlertCircle, Plus, Minus } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { EnhancedDatePicker } from "@/components/ui/date-picker";
import { cn } from "@/lib/utils";
import { add, format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";

const INSTALLMENT_OPTIONS = [
  { value: 2, label: "2x" },
  { value: 3, label: "3x" },
  { value: 4, label: "4x" },
  { value: 5, label: "5x" },
  { value: 6, label: "6x" },
  { value: 7, label: "7x" },
  { value: 8, label: "8x" },
  { value: 9, label: "9x" },
  { value: 10, label: "10x" },
  { value: 11, label: "11x" },
  { value: 12, label: "12x" },
];

const FREQUENCY_OPTIONS = [
  { value: "DAILY", label: "Diário" },
  { value: "WEEKLY", label: "Semanal" },
  { value: "MONTHLY", label: "Mensal" },
  { value: "YEARLY", label: "Anual" },
] as const;

type FrequencyType = typeof FREQUENCY_OPTIONS[number]["value"];
type PaymentType = "CASH" | "INSTALLMENTS" | "RECURRING";

interface InstallmentInfo {
  current: number;
  total: number;
  parentId: string;
}

interface PaymentConditionsProps {
  amount: number;
  transactionType: "INCOME" | "EXPENSE";
  readonly?: boolean;
  installmentInfo?: InstallmentInfo | null;
}

export function PaymentConditions({ amount, transactionType, readonly = false, installmentInfo }: PaymentConditionsProps) {
  const { control, setValue, watch } = useFormContext();

  const formValues = useWatch({
    control,
    name: [
      "installmentType",
      "recurrenceFrequency",
      "recurrenceInterval",
      "recurrenceEndDate",
      "recurrenceCount",
      "date",
      "total_installments",
    ],
  }) as [
    PaymentType | undefined,
    FrequencyType | undefined,
    number | undefined,
    string | undefined,
    number | undefined,
    string | undefined,
    number | undefined,
  ];

  const [installmentType, frequency, interval, endDate, count, startDate, totalInstallments] = formValues;

  const isInstallments = installmentType === "INSTALLMENTS";
  const isRecurring = installmentType === "RECURRING";
  const isIntervalDisabled = frequency === 'DAILY';

  // Auto-populate interval when frequency is DAILY
  useEffect(() => {
    if (isRecurring && frequency === 'DAILY') {
      setValue('recurrenceInterval', 1, {
        shouldValidate: true,
        shouldDirty: true
      });
    }
  }, [frequency, setValue, isRecurring]);

  // Calculate preview dates for recurrence
  const previewDates = useMemo(() => {
    if (!isRecurring || !frequency || !interval || !startDate) return [];

    const dates = [];
    const maxPreview = Math.min(count || 5, 5);
    let currentDate = parseISO(startDate);

    for (let i = 0; i < maxPreview; i++) {
      if (i > 0) {
        switch (frequency) {
          case "DAILY":
            currentDate = add(currentDate, { days: interval });
            break;
          case "WEEKLY":
            currentDate = add(currentDate, { weeks: interval });
            break;
          case "MONTHLY":
            currentDate = add(currentDate, { months: interval });
            break;
          case "YEARLY":
            currentDate = add(currentDate, { years: interval });
            break;
        }
      }

      if (endDate && currentDate > parseISO(endDate)) {
        break;
      }

      dates.push(currentDate);
    }

    return dates;
  }, [isRecurring, frequency, interval, startDate, endDate, count]);

  const installmentAmount = (count: number) => {
    if (count <= 0 || !amount) return 0;
    return amount / count;
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  const handleIntervalChange = (value: string) => {
    const numValue = parseInt(value);
    if (!isNaN(numValue) && numValue > 0 && numValue <= 365) {
      setValue("recurrenceInterval", numValue, { shouldValidate: true });
    }
  };

  const adjustInterval = (adjustment: number) => {
    const newInterval = Math.max(1, Math.min(365, (interval || 1) + adjustment));
    setValue("recurrenceInterval", newInterval, { shouldValidate: true });
  };

  // Get color classes based on transaction type
  const getColorClasses = (isActive: boolean) => {
    if (!isActive) return "border-border hover:border-border bg-background";
    return transactionType === "INCOME"
      ? "border-green-500 bg-green-500/10"
      : "border-red-500 bg-red-500/10";
  };

  const getTextColorClasses = (isActive: boolean) => {
    if (!isActive) return "text-foreground";
    return transactionType === "INCOME" ? "text-green-700" : "text-red-700";
  };

  return (
    <Card className="shadow-sm border-0 bg-card/50 backdrop-blur-sm">
      <CardHeader className="p-4 pb-2">
        <CardTitle className="text-lg font-bold flex items-center gap-2">
          <CreditCard className="h-5 w-5 text-primary" />
          Condições de Pagamento
        </CardTitle>
      </CardHeader>
      <CardContent className="p-4 pt-2 space-y-4">
        {/* Installment Info Banner - shown when editing an installment child */}
        {readonly && installmentInfo && (
          <div
            role="alert"
            aria-live="polite"
            className="mb-4 p-4 bg-info/10 dark:bg-info/5 border border-info/30 dark:border-info/20 rounded-lg"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-info/20 dark:bg-info/10 rounded-full">
                <CreditCard className="h-5 w-5 text-info dark:text-info/80" aria-hidden="true" />
              </div>
              <div className="flex-1">
                <p className="text-2xl font-bold text-info dark:text-info">
                  Parcela {installmentInfo.current} de {installmentInfo.total}
                </p>
              </div>
            </div>
            <p className="text-xs text-info/80 dark:text-info/70 mt-2">
              Ao editar esta parcela, apenas os campos desta parcela serão alterados.
              As outras parcelas da série não serão afetadas.
            </p>
          </div>
        )}

        {/* Tipo de Pagamento - 3 opções */}
        <FormField
          control={control}
          name="installmentType"
          render={({ field }) => (
            <FormItem className="space-y-3">
              <FormLabel className="text-sm font-semibold">
                Como você quer pagar?
              </FormLabel>
              <FormControl>
                <div className="grid grid-cols-3 gap-2">
                  {/* À Vista */}
                  <button
                    type="button"
                    disabled={readonly}
                    aria-disabled={readonly}
                    aria-label={readonly ? "Opção desabilitada: esta é uma parcela de compra parcelada" : "Pagar à vista"}
                    onClick={() => {
                      field.onChange("CASH");
                      setValue("isRecurring", false);
                      setValue("total_installments", undefined);
                      // Limpar campos de recorrência
                      setValue("recurrenceFrequency", undefined);
                      setValue("recurrenceInterval", undefined);
                      setValue("recurrenceEndDate", undefined);
                      setValue("recurrenceCount", undefined);
                    }}
                    className={cn(
                      "flex flex-col items-center justify-center gap-1 p-3 rounded-lg border-2 transition-all duration-200",
                      field.value === "CASH" ? "border-primary bg-primary/5 shadow-md" : "border-border hover:border-border bg-background",
                      readonly ? "opacity-50 cursor-not-allowed" : ""
                    )}
                  >
                    <Wallet className={cn(
                      "h-5 w-5",
                      field.value === "CASH"
                        ? transactionType === "INCOME" ? "text-green-600" : "text-red-600"
                        : "text-muted-foreground"
                    )} />
                    <span className={cn(
                      "text-xs font-semibold",
                      field.value === "CASH"
                        ? transactionType === "INCOME" ? "text-green-700" : "text-red-700"
                        : "text-foreground"
                    )}>
                      À vista
                    </span>
                  </button>

                  {/* Parcelado */}
                  <button
                    type="button"
                    disabled={readonly}
                    aria-disabled={readonly}
                    aria-label={readonly ? "Opção desabilitada: esta é uma parcela de compra parcelada" : "Pagar parcelado"}
                    onClick={() => {
                      field.onChange("INSTALLMENTS");
                      setValue("isRecurring", false);
                      setValue("total_installments", 2);
                      // Limpar campos de recorrência
                      setValue("recurrenceFrequency", undefined);
                      setValue("recurrenceInterval", undefined);
                      setValue("recurrenceEndDate", undefined);
                      setValue("recurrenceCount", undefined);
                    }}
                    className={cn(
                      "flex flex-col items-center justify-center gap-1 p-3 rounded-lg border-2 transition-all duration-200",
                      field.value === "INSTALLMENTS" ? "border-primary bg-primary/5 shadow-md" : "border-border hover:border-border bg-background",
                      readonly ? "opacity-50 cursor-not-allowed" : ""
                    )}
                  >
                    <CreditCard className={cn(
                      "h-5 w-5",
                      field.value === "INSTALLMENTS"
                        ? transactionType === "INCOME" ? "text-green-600" : "text-red-600"
                        : "text-muted-foreground"
                    )} />
                    <span className={cn(
                      "text-xs font-semibold",
                      field.value === "INSTALLMENTS"
                        ? transactionType === "INCOME" ? "text-green-700" : "text-red-700"
                        : "text-foreground"
                    )}>
                      Parcelado
                    </span>
                  </button>

                  {/* Recorrente */}
                  <button
                    type="button"
                    disabled={readonly}
                    aria-disabled={readonly}
                    aria-label={readonly ? "Opção desabilitada: esta é uma parcela de compra parcelada" : "Pagar recorrente"}
                    onClick={() => {
                      field.onChange("RECURRING");
                      setValue("isRecurring", true);
                      setValue("total_installments", undefined);
                    }}
                    className={cn(
                      "flex flex-col items-center justify-center gap-1 p-3 rounded-lg border-2 transition-all duration-200",
                      field.value === "RECURRING" ? "border-primary bg-primary/5 shadow-md" : "border-border hover:border-border bg-background",
                      readonly ? "opacity-50 cursor-not-allowed" : ""
                    )}
                  >
                    <Repeat className={cn(
                      "h-5 w-5",
                      field.value === "RECURRING"
                        ? transactionType === "INCOME" ? "text-green-600" : "text-red-600"
                        : "text-muted-foreground"
                    )} />
                    <span className={cn(
                      "text-xs font-semibold",
                      field.value === "RECURRING"
                        ? transactionType === "INCOME" ? "text-green-700" : "text-red-700"
                        : "text-foreground"
                    )}>
                      Recorrente
                    </span>
                  </button>
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Parcelas (quando parcelado) */}
        {isInstallments && !readonly && (
          <FormField
            control={control}
            name="total_installments"
            render={({ field }) => {
              const currentValue = field.value || 2;

              return (
                <FormItem className="space-y-3">
                  <FormLabel className="text-sm font-semibold">
                    Número de parcelas
                  </FormLabel>
                  <FormControl>
                    <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                      {INSTALLMENT_OPTIONS.map((option) => {
                        const valuePerInstallment = installmentAmount(option.value);
                        const isSelected = currentValue === option.value;

                        return (
                          <button
                            key={option.value}
                            type="button"
                            onClick={() => field.onChange(option.value)}
                            className={cn(
                              "flex flex-col items-center justify-center p-3 rounded-lg border-2 transition-all duration-200",
                              isSelected
                                ? cn("border-primary shadow-md", getColorClasses(true))
                                : "border-border hover:border-border bg-background hover:bg-muted"
                            )}
                          >
                            <span className="font-bold text-lg">
                              {option.label}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {formatCurrency(valuePerInstallment)}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </FormControl>

                  {/* Preview do parcelamento */}
                  {currentValue > 1 && amount > 0 && (
                    <div className="mt-3 p-3 rounded-lg bg-info/10 border border-info/30">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">
                          Total: <strong>{formatCurrency(amount)}</strong> em {currentValue}x de
                        </span>
                        <span className={cn(
                          "font-bold text-base",
                          transactionType === "INCOME" ? "text-green-700" : "text-red-700"
                        )}>
                          {formatCurrency(installmentAmount(currentValue))}
                        </span>
                      </div>
                    </div>
                  )}
                  <FormMessage />
                </FormItem>
              );
            }}
          />
        )}

        {/* Recorrência (quando recorrente) */}
        {isRecurring && (
          <>
            {/* Frequency and Interval */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={control}
                name="recurrenceFrequency"
                render={({ field }) => (
                  <FormItem className="space-y-2">
                    <FormLabel className="text-sm font-semibold">Frequência</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger className="h-10 text-sm rounded-lg border-2 focus-visible:ring-2 focus-visible:ring-primary/20">
                          <SelectValue placeholder="Selecione a frequência" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {FREQUENCY_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={control}
                name="recurrenceInterval"
                render={({ field }) => (
                  <FormItem className="space-y-2">
                    <FormLabel className="text-sm font-semibold flex items-center gap-2">
                      Intervalo
                      {isIntervalDisabled && (
                        <span className="text-xs text-muted-foreground font-normal">
                          (Auto: 1)
                        </span>
                      )}
                    </FormLabel>
                    <FormControl>
                      <div className="flex items-center gap-2">
                        <div className="relative flex-1">
                          <Input
                            type="number"
                            min="1"
                            max="365"
                            placeholder="1"
                            value={field.value || ""}
                            onChange={(e) => handleIntervalChange(e.target.value)}
                            disabled={isIntervalDisabled}
                            className={cn(
                              "h-10 text-sm rounded-lg border-2 focus-visible:ring-2 focus-visible:ring-primary/20 pr-8",
                              isIntervalDisabled && "bg-muted/50 opacity-75 cursor-not-allowed"
                            )}
                          />
                          <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex flex-col">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="h-4 w-4 p-0 hover:bg-transparent"
                              onClick={() => adjustInterval(1)}
                              disabled={isIntervalDisabled}
                            >
                              <Plus className={cn(
                                "h-3 w-3",
                                isIntervalDisabled ? "text-muted-foreground/30" : "text-muted-foreground"
                              )} />
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="h-4 w-4 p-0 hover:bg-transparent"
                              onClick={() => adjustInterval(-1)}
                              disabled={isIntervalDisabled}
                            >
                              <Minus className={cn(
                                "h-3 w-3",
                                isIntervalDisabled ? "text-muted-foreground/30" : "text-muted-foreground"
                              )} />
                            </Button>
                          </div>
                        </div>
                        <span className="text-xs text-muted-foreground whitespace-nowrap">
                          {frequency && FREQUENCY_OPTIONS.find(f => f.value === frequency)?.label.toLowerCase()}
                        </span>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* End Condition */}
            <div className="space-y-3">
              <FormLabel className="text-sm font-semibold">Condição de Término</FormLabel>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={control}
                  name="recurrenceCount"
                  render={({ field }) => (
                    <FormItem className="space-y-2">
                      <FormLabel className="text-xs font-medium text-muted-foreground">
                        Número de ocorrências
                      </FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min="1"
                          max="365"
                          placeholder="Opcional"
                          value={field.value || ""}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value === "") {
                              field.onChange(undefined);
                            } else {
                              const numValue = parseInt(value);
                              if (!isNaN(numValue) && numValue > 0) {
                                field.onChange(numValue);
                              }
                            }
                          }}
                          className="h-10 text-sm rounded-lg border-2 focus-visible:ring-2 focus-visible:ring-primary/20"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={control}
                  name="recurrenceEndDate"
                  render={({ field }) => (
                    <FormItem className="space-y-2">
                      <FormLabel className="text-xs font-medium text-muted-foreground">
                        Data final (opcional)
                      </FormLabel>
                      <FormControl>
                        <EnhancedDatePicker
                          value={field.value}
                          onChange={(date) => {
                            field.onChange(date ? format(date, "yyyy-MM-dd") : undefined);
                          }}
                          placeholder="Selecionar data final"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <p className="text-xs text-muted-foreground">
                <AlertCircle className="inline h-3 w-3 mr-1" />
                Se não especificado, a recorrência continuará indefinidamente
              </p>
            </div>

            {/* Preview */}
            {previewDates.length > 0 && (
              <div className="space-y-2">
                <FormLabel className="text-sm font-semibold flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  Próximas transações
                </FormLabel>
                <ScrollArea className="max-h-32 w-full">
                  <div className="flex flex-wrap gap-2">
                    {previewDates.map((date, index) => (
                      <Badge key={index} variant="secondary" className="text-xs">
                        <Calendar className="h-3 w-3 mr-1" />
                        {format(date, "dd/MM/yyyy", { locale: ptBR })}
                        {index === 0 && (
                          <span className="ml-1 text-muted-foreground">(hoje)</span>
                        )}
                      </Badge>
                    ))}
                    {count && previewDates.length < count && (
                      <Badge variant="outline" className="text-xs">
                        +{count - previewDates.length} mais...
                      </Badge>
                    )}
                  </div>
                </ScrollArea>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
